import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Tabs,
  DataTable,
  Skeleton,
  Modal,
  ConfirmDialog,
  EmptyState,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, TabItem } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import type { CurrencyCode } from '@bbpos/types';
import { approvalService } from '@/services/expense.service';
import type { ApprovalItem, ApprovalType } from '@/types/expense';

type ApprovalRow = ApprovalItem & { id: string };

const TYPE_LABELS: Record<string, string> = {
  EXPENSE: 'Expense',
  DISCOUNT: 'Discount',
  STOCK_TRANSFER: 'Stock Transfer',
  RETURN: 'Return',
  VOID: 'Void',
  CREDIT: 'Credit',
  WASTAGE: 'Wastage',
};

// The API reports the stored operation verb (e.g. discount_override); map it
// onto the coarse type the labels and badges are keyed by.
const OPERATION_TYPES: Record<string, string> = {
  discount_override: 'DISCOUNT',
  void_bill: 'VOID',
  refund_override: 'VOID',
  return_approval: 'RETURN',
  store_credit: 'CREDIT',
  stock_transfer: 'STOCK_TRANSFER',
  // QA IS4-002: wastage above the recorder's limit, its stock held until reviewed.
  wastage: 'WASTAGE',
};
const typeKey = (type: string): string => OPERATION_TYPES[type] ?? type;

const TYPE_BADGE_CLASSES: Record<string, string> = {
  EXPENSE: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300',
  DISCOUNT: 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
  STOCK_TRANSFER: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  RETURN: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
  VOID: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
  CREDIT: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  WASTAGE: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
};

// ─── Icons ───────────────────────────────────────────────────────────────────

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// Human-readable identification of the request a shortcut/dialog will act on, so
// the confirm dialog names the exact request (type · submitter · amount) instead
// of a generic "the selected request". Approval is irreversible, so the user must
// be able to verify which request they are confirming.
export function describeApproval(
  row: Pick<ApprovalItem, 'type' | 'requested_by_name' | 'amount'>,
  // Defaulted so the existing INR callers and tests are unaffected. This is a
  // module-level pure function, so it cannot read useTenantCurrency() itself —
  // ApprovalsContent passes the tenant currency in.
  currency: CurrencyCode = 'INR',
): string {
  const parts: string[] = [TYPE_LABELS[typeKey(row.type)] ?? row.type];
  if (row.requested_by_name) parts.push(row.requested_by_name);
  if (row.amount != null) parts.push(formatCurrency(row.amount, currency));
  return parts.join(' · ');
}

export interface BatchApprovalOutcome {
  // IDs that were approved successfully — cleared from the selection.
  succeededIds: string[];
  // IDs that failed — kept selected so the manager can retry/investigate.
  failedIds: string[];
  toast: {
    title: string;
    description?: string;
    variant: 'success' | 'warning' | 'error';
  };
}

// Summarizes a batch-approve run, preserving each failure's identity and reason
// instead of collapsing them into a bare count. Successful IDs are separated from
// failed IDs so the caller can keep only the failures selected for retry, and the
// toast description lists every failed request with its specific backend reason
// (e.g. "already approved" 409 vs. non-PENDING 400 vs. a genuine 500).
export function summarizeBatchApproval(
  items: { id: string; label: string }[],
  results: PromiseSettledResult<unknown>[],
): BatchApprovalOutcome {
  const succeededIds: string[] = [];
  const failures: { id: string; label: string; reason: string }[] = [];

  results.forEach((result, i) => {
    const item = items[i];
    if (result.status === 'fulfilled') {
      succeededIds.push(item.id);
    } else {
      const reason =
        result.reason instanceof Error ? result.reason.message : String(result.reason);
      failures.push({ id: item.id, label: item.label, reason });
    }
  });

  const total = items.length;
  const failed = failures.length;

  if (failed === 0) {
    return {
      succeededIds,
      failedIds: [],
      toast: {
        title: `${total} request${total === 1 ? '' : 's'} approved`,
        variant: 'success',
      },
    };
  }

  return {
    succeededIds,
    failedIds: failures.map((f) => f.id),
    toast: {
      title: `${total - failed} approved, ${failed} failed`,
      description: failures.map((f) => `${f.label} — ${f.reason}`).join('\n'),
      variant: failed === total ? 'error' : 'warning',
    },
  };
}

// Builds the approval tab items. The "All" badge must reflect the total pending
// across *all* types (allTotal), independent of which type tab is active — never
// the active tab's filtered count.
export function buildApprovalTabs(allTotal: number): TabItem[] {
  return [
    { id: 'all', label: 'All', badge: allTotal > 0 ? allTotal : undefined },
    { id: 'EXPENSE', label: 'Expenses' },
    // L07: nothing creates void / credit / stock approvals, so those tabs were
    // always empty; bring a tab back with the request that fills it.
    { id: 'DISCOUNT', label: 'Discounts' },
    { id: 'RETURN', label: 'Returns' },
    { id: 'WASTAGE', label: 'Wastage' },
  ];
}

function ApprovalsContent() {
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const activeTab = searchParams.get('tab') ?? 'all';
  const page = Number(searchParams.get('page') ?? '1');

  const [confirmApproveId, setConfirmApproveId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ApprovalRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // Approving needs a justification; it is kept as the request's review note.
  const [approveNote, setApproveNote] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmBatchOpen, setConfirmBatchOpen] = useState(false);

  function setTab(id: string) {
    setSearchParams({ tab: id, page: '1' });
  }

  function setPage(p: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(p));
      return next;
    });
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['approvals', activeTab, page],
    queryFn: () =>
      approvalService.list({
        type: activeTab === 'all' ? undefined : (activeTab as ApprovalType),
        page,
        limit: 20,
      }),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });

  // Dedicated all-types total — always unfiltered so the header "pending" figure
  // and the "All" tab badge stay stable regardless of the active type tab.
  const { data: allTotalData } = useQuery({
    queryKey: ['approvals', 'all-total'],
    queryFn: () => approvalService.list({ page: 1, limit: 1 }),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (error) toast({ title: (error as Error).message, variant: 'error' });
  }, [error]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['approvals'] });

  const approveMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => approvalService.approve(id, note),
    onSuccess: () => {
      invalidate();
      setConfirmApproveId(null);
      setApproveNote('');
      toast({ title: 'Approved successfully', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const batchApproveMutation = useMutation({
    mutationFn: async (items: { id: string; label: string }[]) => {
      const results = await Promise.allSettled(
        items.map((item) => approvalService.approve(item.id)),
      );
      return summarizeBatchApproval(items, results);
    },
    onSuccess: ({ failedIds, toast: t }) => {
      invalidate();
      setConfirmBatchOpen(false);
      // Keep only the failed requests selected so the manager can see and retry
      // them; deselect the ones that succeeded.
      setSelectedIds(failedIds);
      toast(t);
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      approvalService.reject(id, reason),
    onSuccess: () => {
      invalidate();
      setRejectTarget(null);
      setRejectReason('');
      toast({ title: 'Rejected successfully', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const approvals = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  // The exact request the approve dialog will action — used to name it so the
  // user can verify before confirming this irreversible operation.
  const approveTarget = confirmApproveId
    ? approvals.find((a) => a.id === confirmApproveId) ?? null
    : null;
  // All-types pending total — drives the header count and the "All" tab badge.
  const allTotal = allTotalData?.meta?.total ?? 0;

  // Tab items per PAGES.md §3.38: All / Expenses / Voids / Returns / Credits / Stock
  const TABS = buildApprovalTabs(allTotal);

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'a' && approvals.length > 0) {
        setConfirmApproveId(approvals[0].id);
      }
      if (e.key === 'r' && approvals.length > 0) {
        setRejectTarget({ ...approvals[0], id: approvals[0].id });
        setRejectReason('');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [approvals]);

  const columns: ColumnDef<ApprovalRow>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (_v, row) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_BADGE_CLASSES[typeKey(row.type)] ?? ''}`}
        >
          {TYPE_LABELS[typeKey(row.type)] ?? row.type}
        </span>
      ),
    },
    {
      key: 'summary',
      header: 'Description',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400">
          {row.summary
            ? row.summary.length > 60
              ? row.summary.slice(0, 60) + '\u2026'
              : row.summary
            : '\u2014'}
        </span>
      ),
    },
    {
      key: 'requested_by_name',
      header: 'Submitted By',
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300">{row.requested_by_name ?? '\u2014'}</span>
      ),
    },
    {
      key: 'created_at',
      header: 'Date',
      render: (_v, row) => (
        <span className="text-sm text-neutral-500 tabular-nums">
          {new Date(row.created_at).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
          {/* No money amount (e.g. a percentage discount) reads "—", never 0. */}
          {row.amount == null ? '—' : formatCurrency(row.amount, currency)}
        </span>
      ),
    },
    {
      key: 'id',
      header: 'Actions',
      render: (_v, row) => (
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setConfirmApproveId(row.id)}
            disabled={approveMutation.isPending}
          >
            Approve
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setRejectTarget(row);
              setRejectReason('');
            }}
            disabled={rejectMutation.isPending}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  // ── Loading ────────────────────────────────────────────────────────────────

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <Skeleton variant="rect" height={48} className="rounded-xl mb-4" />
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={48} className="rounded-lg" />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-brand-600 to-violet-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Approvals</h1>
            <p className="text-brand-100 mt-1">Pending approval requests across all modules</p>
          </div>
          {allTotal > 0 && (
            <div className="flex items-center gap-2 bg-white/20 rounded-lg px-4 py-2">
              <span className="text-3xl font-bold tabular-nums">{allTotal}</span>
              <span className="text-sm text-brand-100">pending</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4">
        <Tabs
          items={TABS}
          activeId={activeTab}
          onChange={setTab}
        />
      </div>

      {/* Table or Empty */}
      {!isLoading && approvals.length === 0 ? (
        <EmptyState
          variant="table"
          icon={<CheckCircleIcon className="h-full w-full" />}
          title="No pending approvals"
          description="All approval requests have been processed"
        />
      ) : (
        <div className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm rounded-xl shadow-md animate-slide-up">
          <div data-testid="approvals-table">
            <DataTable<ApprovalRow>
              columns={columns}
              data={approvals.map((a) => ({ ...a, id: a.id }))}
              loading={isLoading}
              totalCount={total}
              currentPage={page}
              pageSize={20}
              onPageChange={setPage}
              selectable
              selectedRows={selectedIds}
              onSelectionChange={setSelectedIds}
              bulkActions={[
                {
                  label: 'Batch Approve',
                  onClick: (ids) => {
                    setSelectedIds(ids);
                    setConfirmBatchOpen(true);
                  },
                },
              ]}
              emptyState={{
                title: 'No pending approvals',
                description: 'All approval requests have been processed',
              }}
            />
          </div>
        </div>
      )}

      {/* Batch Approve Confirm Dialog */}
      <ConfirmDialog
        open={confirmBatchOpen}
        onCancel={() => setConfirmBatchOpen(false)}
        onConfirm={() =>
          batchApproveMutation.mutate(
            selectedIds.map((id) => {
              const row = approvals.find((a) => a.id === id);
              return { id, label: row ? describeApproval(row, currency) : id };
            }),
          )
        }
        title={`Approve ${selectedIds.length} request${selectedIds.length === 1 ? '' : 's'}?`}
        description="The selected requests will be approved. This action cannot be undone."
        confirmLabel="Approve All"
        variant="warning"
        loading={batchApproveMutation.isPending}
      />

      {/* Approve Confirm Dialog */}
      <ConfirmDialog
        open={!!confirmApproveId}
        onCancel={() => { setConfirmApproveId(null); setApproveNote(''); }}
        onConfirm={() => {
          if (confirmApproveId && approveNote.trim().length >= 3) {
            approveMutation.mutate({ id: confirmApproveId, note: approveNote.trim() });
          }
        }}
        title="Approve Request?"
        description={
          approveTarget
            ? `You are about to approve ${describeApproval(approveTarget, currency)}. This action cannot be undone.`
            : 'This action will approve the selected request and cannot be undone.'
        }
        confirmLabel="Approve"
        variant="warning"
        loading={approveMutation.isPending}
        confirmDisabled={approveNote.trim().length < 3}
      >
        <textarea
          value={approveNote}
          onChange={(e) => setApproveNote(e.target.value)}
          placeholder="Why is this approved? (required, 3+ characters)"
          aria-label="Justification"
          rows={3}
          className="w-full mt-3 px-3 py-2 text-sm rounded-lg bg-white/60 dark:bg-neutral-800/60 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none shadow-sm transition-all duration-200"
        />
      </ConfirmDialog>

      {/* Reject Modal */}
      <Modal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title="Reject Request"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {rejectTarget
              ? `Please provide a reason for rejecting ${describeApproval(rejectTarget, currency)}.`
              : 'Please provide a reason for rejecting this request.'}
          </p>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason for rejection…"
            rows={3}
            className="w-full px-3 py-2 text-sm rounded-lg bg-white/60 dark:bg-neutral-800/60 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none shadow-sm transition-all duration-200"
          />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (rejectTarget && rejectReason.trim()) {
                  rejectMutation.mutate({ id: rejectTarget.id, reason: rejectReason.trim() });
                }
              }}
              loading={rejectMutation.isPending}
              disabled={!rejectReason.trim()}
            >
              Reject
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export function ApprovalsPage() {
  return (
    <PageContainer
      title="Approvals"
      breadcrumbs={[{ label: 'Approvals' }]}
    >
      <ErrorBoundary>
        <ApprovalsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
